import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Alert, Typography, Card, CardMedia, CardContent, Container } from '@mui/material';
import { supabase } from '../supabase/client';

const StudentCard = ({ student }) => (
  <Card
    sx={{
      width: 'calc(16.666% - 14px)',
      minWidth: '180px',
      maxWidth: '220px',
      height: '400px',
      border: '2px solid #1e3a5f',
      boxShadow: 2,
      bgcolor: 'white',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {student.picture_url && (
      <CardMedia
        component='img'
        height='240'
        image={student.picture_url}
        alt={`${student.first_name} ${student.last_name}`}
        sx={{ objectFit: 'cover' }}
      />
    )}
    <CardContent sx={{ py: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography
        variant='h6'
        component='div'
        sx={{
          color: '#1e3a5f',
          fontWeight: 'bold',
          mb: 1,
          fontSize: '1.1rem',
        }}
      >
        {student.first_name} {student.last_name}
      </Typography>
      <Typography variant='body2' sx={{ color: '#1e3a5f', fontSize: '0.9rem', lineHeight: 1.4 }}>
        {student.achievements}
      </Typography>
    </CardContent>
  </Card>
);

const StudentSection = ({ title, students, emptyMessage }) => (
  <Box sx={{ mb: 6 }}>
    <Typography
      variant='h5'
      sx={{
        color: '#1e3a5f',
        fontWeight: 'bold',
        mb: 3,
        letterSpacing: '1px',
      }}
    >
      {title}
    </Typography>

    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {students.length === 0 ? (
        <Typography color='text.secondary'>{emptyMessage}</Typography>
      ) : (
        students.map((student) => <StudentCard key={student.id} student={student} />)
      )}
    </Box>
  </Box>
);

const Home = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.from('students').select('*').order('created_at', { ascending: false });

      if (error) throw error;
      setStudents(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
        <CircularProgress />
      </Box>
    );
  }

  const internationalStudents = students.filter((s) => s.achievement_type === 'International');
  const nationalStudents = students.filter((s) => s.achievement_type === 'National');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#e8f0f8' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: '#1e3a5f',
          minHeight: '120px',
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            bgcolor: '#1e3a5f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 4,
            py: 2,
          }}
        >
          <img src='/logo.png' alt='School Logo' style={{ height: '100px', width: 'auto' }} />
        </Box>

        {/* Title Section */}
        <Box
          sx={{
            flex: 1,
            bgcolor: '#ffd700',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 4,
            py: 3,
          }}
        >
          <Typography
            variant='h4'
            sx={{
              color: '#1e3a5f',
              fontWeight: 'bold',
              textAlign: 'center',
              textTransform: 'uppercase',
            }}
          >
            Нийслэлийн 11 дүгээр сургуулийн хүндэт самбар
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Container maxWidth='xl' sx={{ py: 4 }}>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <StudentSection
          title='ОЛОН УЛСЫН ОЛИМПИАДЫН МЕДАЛЬТ СУРАГЧИД'
          students={internationalStudents}
          emptyMessage='Олон улсын медальт сурагч олдсонгүй.'
        />

        <StudentSection
          title='УЛСЫН ОЛИМПИАДЫН МЕДАЛЬТ СУРАГЧИД'
          students={nationalStudents}
          emptyMessage='Үндэсний медальт сурагч олдсонгүй.'
        />
      </Container>
    </Box>
  );
};

export default Home;
